import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import html2pdf from 'html2pdf.js';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend,
    LabelList
} from "recharts";
import { fetchMoodReport } from "./store";
import { Card, Table, Form, Button, FormControl, Modal } from 'react-bootstrap';
import { FaSearch, FaDownload, FaTimes, FaUser, FaVenusMars, FaMobileAlt, FaBriefcase, FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight, FaChevronUp, FaChevronDown } from "react-icons/fa";
import { MdOutlineMood, MdOutlineMonitorHeart, MdOutlineBolt } from "react-icons/md";
import Swal from 'sweetalert2';
import { ComponentLoader } from '../common/Loader';

const MoodAnalytics = () => {
    const dispatch = useDispatch();
    const [reportData, setReportData] = useState({
        summary: { totalMoodLogs: null, activeUsers: null, mostCommonMood: null, highStressLogs: null },
        tableData: [],
        barChartData: [],
        moodPieData: [],
        energyPieData: [],
        stressPieData: [],
        allProfessions: []
    });

    const [totalCount, setTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [goToPage, setGoToPage] = useState('');
    const [sortBy, setSortBy] = useState('createdDate:desc');
    const [prevFilters, setPrevFilters] = useState(null);

    const [showExportModal, setShowExportModal] = useState(false);
    const [exportOption, setExportOption] = useState("currentPage"); 
    const [exportRange, setExportRange] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const today = new Date().toISOString().split('T')[0];

    const initialFilters = {
        gender: 'All',
        ageRange: 'All',
        deviceType: 'All',
        profession: 'All',
        mood: 'All',
        energy: 'All',
        stress: 'All',
        searchTerm: '',
        startDate: '',
        endDate: ''
    };

    const [filters, setFilters] = useState(initialFilters);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimeoutRef = useRef(null);
    const isFetching = useRef(false);
    const lastFetchParams = useRef(null);

    const moodEmojis = {
        "Happy": "😁",
        "Sadness": "😔",
        "Fear": "😨",
        "Anger": "😡",
        "Cheerful": "💐",
        "Romantic": "🥰",
        "Madness": "🤯",
        "Calm": "😌",
        "NegativeMood": "🤬",
        "Anxious": "🥴",
        "Irritability": "😬",
        "Disgust": "🤢"
    };

    const energyMap = { "1": "Low", "2": "Mild", "3": "Steady", "4": "High", "5": "Peak" };
    const stressMap = { "1": "Low", "2": "Moderate", "3": "High", "4": "Very High", "5": "Extreme" };
    const energyColors = { "Low": "#ef4444", "Mild": "#f59e0b", "Steady": "#7feecdff", "High": "#10b981", "Peak": "#3b82f6" };
    const stressColors = { "Low": "#10b981", "Moderate": "#3b82f6", "High": "#f59e0b", "Very High": "#ef4444", "Extreme": "#991b1b" };
    const moodColors = { "Happy": "#22c55e", "Cheerful": "#4ade80", "Calm": "#14b8a6", "Romantic": "#f472b6", "Anxious": "#fbbf24", "Madness": "#f97316", "Sadness": "#64748b", "Fear": "#6366f1", "Anger": "#ef4444", "Irritability": "#dc2626", "Disgust": "#b91c1c", "NegativeMood": "#991b1b" };

    const fetchData = (isPageChange = false) => {
        if (isFetching.current) return;
        setLoading(true);
        isFetching.current = true;

        const currentFilterSet = JSON.stringify({ ...filters, debouncedSearch, sortBy });
        const skipCharts = isPageChange && currentFilterSet === prevFilters;

        const payload = {
            ...filters,
            search: debouncedSearch,
            page: currentPage,
            limit: itemsPerPage,
            skipCharts,
            sortBy: sortBy.split(':')[0],
            sortOrder: sortBy.split(':')[1]
        };

        dispatch(fetchMoodReport(payload))
            .then((response) => {
                if (response.payload && response.payload.success) {
                    const newData = response.payload.tableData || [];
                    
                    if (skipCharts) {
                        setReportData(prev => ({
                            ...prev,
                            tableData: newData,
                            allProfessions: response.payload.allProfessions || prev.allProfessions
                        }));
                    } else {
                        setReportData(response.payload);
                        setPrevFilters(currentFilterSet);
                    }

                    setTotalCount(response.payload.totalCount || 0);
                }
                setLoading(false);
                isFetching.current = false;
            })
            .catch((error) => {
                console.error('Error fetching mood report:', error);
                setLoading(false);
                isFetching.current = false;
            });
    };

    useEffect(() => {
        const isOneDateMissing = (filters.startDate && !filters.endDate) || (!filters.startDate && filters.endDate);
        if (isOneDateMissing) return;

        const currentParams = JSON.stringify({ ...filters, debouncedSearch, sortBy, currentPage, itemsPerPage });
        if (lastFetchParams.current === currentParams) return; 
        lastFetchParams.current = currentParams;

        const currentFilterSet = JSON.stringify({ ...filters, debouncedSearch, sortBy });
        const isPageChange = currentFilterSet === prevFilters;

        fetchData(isPageChange);
    }, [dispatch, debouncedSearch, filters.gender, filters.ageRange, filters.deviceType, filters.profession, filters.mood, filters.energy, filters.stress, filters.startDate, filters.endDate, sortBy, currentPage, itemsPerPage]);

    const handleGoToPage = () => {
        const maxPages = Math.ceil(totalCount / itemsPerPage);
        const pageNum = parseInt(goToPage, 10);
        if (!pageNum || pageNum < 1 || pageNum > maxPages) {
            Swal.fire('Invalid Page', `Total pages: ${maxPages}`, 'warning');
            return;
        }
        setCurrentPage(pageNum);
        setGoToPage('');
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleSortChange = (newVal) => {
        setSortBy(newVal);
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = (newVal) => {
        setItemsPerPage(Number(newVal));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setFilters(prev => ({ ...prev, searchTerm: val }));

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(val);
            setCurrentPage(1);
        }, 500);
    };

    const clearFilters = () => {
        setFilters(initialFilters);
        setDebouncedSearch('');
        setCurrentPage(1);
    };


    const formatTime = (dateStr) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
    };



    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' +
            date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const getMoodEmoji = (mood) => moodEmojis[mood] || "😌";

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
        if (percent === 1) {
            return (
                <text x={cx} y={cy} fill="white" textAnchor="middle" dominantBaseline="central" fontSize="12" fontWeight="700">
                    100%
                </text>
            );
        }

        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="12" fontWeight="700">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };


    const generateCSV = (data) => {
        if (!data || data.length === 0) return;

        const headers = ["User ID", "Gender", "Age", "Profession", "Mood", "Energy / Stress", "Date & Time"];
        const rows = data.map(item => [
            item.userId || "—",
            item.userGender || "—",
            item.userAge || "—",
            item.userProfession || "—",
            item.mood || "—",
            `${item.energy || "—"} / ${item.stress || "—"}`,
            formatDate(item.createdDate)
        ]);

        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Mood_Analytics_Report_Export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const generatePDF = (data) => {
        if (!data || data.length === 0) return;

        const totalRecords = data.length;
        const generatedDate = new Date().toLocaleDateString('en-GB');
        const generatedTime = new Date().toLocaleTimeString('en-US', { hour12: false });

        // ✅ Changed to 8 rows per page (as per your requirement)
        const rowsPerPage = 8;
        const totalTablePages = Math.ceil(totalRecords / rowsPerPage);
        const totalPDFPages = 1 + totalTablePages;   // 1 summary + table pages

        const styles = `
              <style>
                  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
                  
                  body { 
                      font-family: 'Poppins', sans-serif; 
                      color: #1e293b; 
                      margin: 0; 
                      padding: 0; 
                  }
                  
                  .report-container {
                      min-height: 275mm;                    /* Changed from height */
                      display: flex;
                      flex-direction: column;
                      background: #fff;
                      padding: 40px 30px 20px;
                      box-sizing: border-box;
                      page-break-after: always;
                  }
                  
                  .exp-page-header {
                      display: flex;
                      justify-content: space-between;
                      align-items: flex-start;
                      margin-bottom: 40px;
                  }
                  
                  .brand-logo { 
                      color: #42da9f; 
                      font-weight: 800; 
                      font-size: 26px; 
                  }
                  .brand-sub { 
                      color: #e05923; 
                      font-size: 11px; 
                      text-transform: uppercase; 
                  }
                  
                  .report-title { 
                      text-align: center; 
                      margin: 30px 0 50px; 
                  }
                  .report-title h1 { 
                      font-size: 28px; 
                      font-weight: 800; 
                      text-transform: uppercase; 
                      color: #0f172a; 
                  }
                  .summary-word::after { 
                      content: ''; 
                      position: absolute; 
                      bottom: -8px; 
                      left: 0; 
                      width: 100%; 
                      height: 3.5px; 
                      background: #f70c0c; 
                  }
                  
                  .section-title { 
                      color: #f70c0c; 
                      font-weight: 800; 
                      font-size: 17px; 
                      border-left: 4px solid #f70c0c; 
                      padding-left: 15px; 
                  }
                  .section-text { 
                      font-size: 13px; 
                      color: #475569; 
                      line-height: 1.8; 
                      text-align: justify; 
                  }
                  
                  .report-table { 
                      width: 100%; 
                      border-collapse: collapse; 
                      margin-top: 20px; 
                      border: 1px solid #e2e8f0; 
                      border-radius: 12px; 
                      overflow: hidden;
                  }
                  .report-table th { 
                      background: #f8fafc; 
                      padding: 14px; 
                      font-size: 11px; 
                      font-weight: 700; 
                      text-transform: uppercase; 
                  }
                  .report-table td { 
                      padding: 13px 10px; 
                      font-size: 12px; 
                      border-bottom: 1px solid #f1f5f9; 
                      text-align: center; 
                  }
                  
                  /* ✅ Prevent row from breaking across pages */
                  .report-table tr { 
                      page-break-inside: avoid; 
                      page-break-after: auto; 
                  }
                  
                  .status-badge { 
                      font-weight: 800; 
                      color: #f70c0c; 
                      text-transform: uppercase; 
                      font-size: 10px; 
                  }
                  
                  .page-footer {
                      margin-top: auto;                    /* ✅ Forces footer to bottom */
                      text-align: center; 
                      font-size: 10px; 
                      color: #94a3b8; 
                      border-top: 1.5px solid #f1f5f9; 
                      padding-top: 15px;
                      text-transform: uppercase; 
                      letter-spacing: 1px;
                  }
              </style>
          `;

        let htmlContent = '';

        // ==================== PAGE 1: SUMMARY ====================
        htmlContent += `
              <div class="report-container">
                  <div class="exp-page-header">
                      <div>
                          <div class="brand-logo">MedHorizone</div>
                          <div class="brand-sub">Healthcare Tracking System</div>
                      </div>
                      <div style="text-align:right">
                          <div style="font-weight:800">Admin</div>
                          <div>${generatedDate} | ${generatedTime}</div>
                      </div>
                  </div>
                  
                  <div class="report-title">
                      <h1>MOOD ANALYTICS <span class="summary-word">SUMMARY</span> REPORT</h1>
                  </div>
                  
                  <div style="margin-bottom:40px">
                      <div class="section-title">1. Executive Summary</div>
                      <p class="section-text">
                          This report provides a comprehensive overview of the mood analytics metrics recorded within the MedHorizone system for the current period. The data highlights significant trends in mood analytics, patient engagement, and overall system utilization. Our primary focus remains on early detection of mood analytics risks and maintaining stable mood analytics levels across all age demographics.
                      </p>
                  </div>
                  
                  <div style="margin-bottom:40px">
                      <div class="section-title">2. Patient Demographics & Engagement</div>
                      <p class="section-text">
                          During this reporting cycle, we observed a steady engagement in active users tracking their vitals daily. The majority of our active user base falls within the 30-50 age range, indicating a growing health consciousness among middle-aged patients. Gender distribution remains balanced, with active participation across both male and female users.
                      </p>
                  </div>
                  
                  <div style="margin-bottom:40px">
                      <div class="section-title">3. Recommendations</div>
                      <p class="section-text">
                          Based on the current data, it is recommended to decrease the alone time, it increases the risk of mood swings. Always try to be with friends and family. Try to do yoga and meditation to improve your mood.
                      </p>
                  </div>
                  
                  <div class="page-footer">
                      Generated by MedHorizone Healthcare System - Confidential Document<br>
                      Page 1 of ${totalPDFPages}
                  </div>
              </div>
          `;

        // ==================== TABLE PAGES (8 rows each) ====================
        for (let p = 0; p < totalTablePages; p++) {
            const start = p * rowsPerPage;
            const pageData = data.slice(start, start + rowsPerPage);

            htmlContent += `
                  <div class="report-container">
                      <div class="exp-page-header">
                          <div>
                              <div class="brand-logo" style="font-size:22px;">MedHorizone</div>
                              <div class="brand-sub">Healthcare Tracking System</div>
                          </div>
                          <div style="text-align:right">
                              <div>${generatedDate}</div>
                          </div>
                      </div>
                      
                      <div style="margin-bottom:20px">
                          <div class="section-title" style="font-size:16px;">
                              Mood Analytics Overview - Page ${p + 1}
                          </div>
                      </div>
                      
                      <table class="report-table">
                          <thead>
                              <tr>
                                  <th style="width:5%">#</th>
                                  <th style="width:15%">User ID</th>
                                  <th style="width:15%">Age/Gender</th>
                                  <th style="width:15%">Profession</th>
                                  <th style="width:15%">Mood</th>
                                  <th style="width:12%">Energy/Stress</th>
                                  <th style="width:13%">Date</th>
                              </tr>
                          </thead>
                          <tbody>
                              ${pageData.map((item, idx) => `
                                  <tr>
                                      <td>${start + idx + 1}</td>
                                      <td style="font-weight:600;">${item.userId || '—'}</td>
                                      <td>${item.userAge || '—'} / ${item.userGender ? item.userGender.charAt(0).toUpperCase() : '—'}</td>
                                      <td>${item.userProfession || '—'}</td>
                                      <td>${item.mood || '—'}</td>
                                      <td>${item.energy || '—'} / ${item.stress || '—'}</td>
                                      <td>${formatDate(item.createdDate)}</td>
                                  </tr>
                              `).join('')}
                          </tbody>
                      </table>
                      
                      <div class="page-footer">
                          Generated by MedHorizone Healthcare System - Confidential Document<br>
                          Page ${p + 2} of ${totalPDFPages}
                      </div>
                  </div>
              `;
        }

        // ==================== GENERATE PDF ====================
        const element = document.createElement('div');
        element.innerHTML = styles + htmlContent;

        const opt = {
            margin: [10, 15, 10, 15],
            filename: `MedHorizone_Mood_Analytics_Report_${generatedDate.replace(/\//g, '-')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, letterRendering: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save().then(() => {
            setIsExporting(false);
            Swal.fire('Success', 'PDF report generated successfully.', 'success');
        });
        setIsExporting(false);
    };

    const performExport = async (format) => {
        setIsExporting(true);
        try {
            let exportData = [];
            const [sBy, sOrd] = sortBy.split(':');

            if (exportOption === "currentPage") {
                exportData = reportData.tableData;
            } else if (exportOption === "allPages") {
                const response = await dispatch(fetchMoodReport({
                    ...filters,
                    search: debouncedSearch,
                    page: 1,
                    limit: 10000, 
                    sortBy: sBy,
                    sortOrder: sOrd,
                    skipCharts: true
                }));
                if (response.payload && response.payload.success) {
                    exportData = response.payload.tableData;
                }
            } else if (exportOption === "pageRange") {
                const [start, end] = exportRange.split("-").map(n => parseInt(n.trim()));
                const maxPages = Math.ceil(totalCount / itemsPerPage);

                if (!start || !end || start > end) {
                    Swal.fire("Invalid Range", "Please enter a valid page range (e.g., 1-5).", "error");
                    setIsExporting(false);
                    return;
                }

                if (end > maxPages) {
                    Swal.fire("Invalid Range", `The end page cannot exceed the total number of pages (${maxPages}).`, "warning");
                    setIsExporting(false);
                    return;
                }

                const response = await dispatch(fetchMoodReport({
                    ...filters,
                    search: debouncedSearch,
                    page: start,
                    limit: (end - start + 1) * itemsPerPage,
                    sortBy: sBy,
                    sortOrder: sOrd,
                    skipCharts: true
                }));
                if (response.payload && response.payload.success) {
                    exportData = response.payload.tableData;
                }
            }

            if (!exportData || exportData.length === 0) {
                Swal.fire("No Data", "No data found for the selected range.", "warning");
            } else {
                if (format === "csv") {
                    generateCSV(exportData);
                } else {
                    generatePDF(exportData);
                }
            }
        } catch (err) {
            console.error("Export failed:", err);
            Swal.fire("Error", "Failed to export data.", "error");
        } finally {
            setIsExporting(false);
            setShowExportModal(false);
        }
    };

    // Shared Styles
    const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: '6px', flex: '1' };
    const filterLabelStyle = { fontSize: '11px', fontWeight: '700', color: '#414347', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '2px' };
    const inputWrapperStyle = { position: 'relative', display: 'flex', alignItems: 'center' };
    const iconStyle = { position: 'absolute', left: '10px', fontSize: '14px', color: '#94a3b8', pointerEvents: 'none' };
    const modernInputStyle = { width: '100%', padding: '9px 12px 9px 32px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', color: '#1e293b', background: '#f8fafc', transition: 'all 0.2s', outline: 'none' };

    const paginationBtnStyle = (active, disabled) => ({
        padding: '8px 14px',
        border: '1px solid #e2e8f0',
        background: active ? '#4f46e5' : 'white',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: 700,
        color: active ? 'white' : '#475569',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s',
        boxShadow: active ? '0 2px 4px rgba(79, 70, 229, 0.2)' : 'none'
    });

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    const renderSortHeader = (label, field, width) => (
        <th className="sticky-header-th" style={{ width }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span>{label}</span>
                <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', fontSize: '11px', gap: '4px' }}>
                    <FaChevronUp color={sortBy === `${field}:asc` ? '#10b981' : '#cbd5e1'} onClick={() => handleSortChange(`${field}:asc`)} />
                    <FaChevronDown color={sortBy === `${field}:desc` ? '#10b981' : '#cbd5e1'} onClick={() => handleSortChange(`${field}:desc`)} />
                </span>
            </div>
        </th>
    );

    return (
        <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
            <style>{`
                .sticky-table { border-collapse: separate !important; border-spacing: 0 !important; overflow: visible !important; position: relative !important; }
                .sticky-header-th { position: sticky !important; top: -1px !important; z-index: 11 !important; background: #f9fafb !important; border-bottom: 1px solid #e5e7eb !important; padding: 14px 16px !important; font-size: 12px !important; font-weight: 700 !important; color: #475569 !important; text-align: center !important; }
                .scroll-container { max-height: 600px; overflow-y: auto !important; overflow-x: auto !important; position: relative !important; }
                @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
            `}</style>
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontWeight: 700, color: '#1a1a1a', fontSize: '24px' }}>Mood Analytics</h4>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '32px' }}>
                {[
                    { title: 'Total Mood Logs', value: reportData.summary.totalMoodLogs, color: '#e0f2fe', iconColor: '#0369a1', label: 'TOTAL MOOD LOGS' },
                    { title: 'Active Users', value: reportData.summary.activeUsers, color: '#dcfce7', iconColor: '#15803d', label: 'ACTIVE USERS' },
                    { title: 'Most Common Mood', value: reportData.summary.mostCommonMood ? `${reportData.summary.mostCommonMood} ${getMoodEmoji(reportData.summary.mostCommonMood)}` : (reportData.summary.mostCommonMood === null ? null : 'N/A'), color: '#fee2e2', iconColor: '#b91c1c', label: 'MOST COMMON MOOD' },
                    { title: 'High Stress Logs', value: reportData.summary.highStressLogs, color: '#f3e8ff', iconColor: '#7e22ce', label: 'HIGH STRESS LOGS' }
                ].map((stat, i) => (
                    <div key={i} style={{ flex: '1', minWidth: '220px', background: stat.color, borderRadius: '16px', padding: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', border: '1px solid rgba(255,255,255,0.3)', minHeight: '120px' }}>
                        <div style={{ fontSize: '11px', color: stat.iconColor, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.8 }}>{stat.label}</div>
                        <div style={{ fontSize: '32px', fontWeight: 900, color: '#1e293b', marginTop: '12px' }}>
                            {stat.value !== null ? stat.value : <ComponentLoader color={stat.iconColor} size="24px" padding="0" />}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters Section */}
            <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', marginBottom: '24px', overflow: 'hidden' }}>
                <Card.Body style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Reading Date Range:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Form.Control
                                type="date"
                                value={filters.startDate}
                                max={filters.endDate || today}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', width: '160px', color: '#4f46e5', fontWeight: 600 }}
                            />
                            <span style={{ color: '#94a3af', fontWeight: 600 }}>to</span>
                            <Form.Control
                                type="date"
                                value={filters.endDate}
                                min={filters.startDate}
                                max={today}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', width: '160px', color: '#4f46e5', fontWeight: 600 }}
                            />
                        </div>
                        <button
                            onClick={clearFilters}
                            style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 800, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
                            onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
                        >
                            <FaTimes /> CLEAR FILTERS
                        </button>
                        <span style={{ marginLeft: 'auto', padding: '8px 20px', background: '#e0e7ff', color: '#4338ca', borderRadius: '24px', fontSize: '13px', fontWeight: 800 }}>
                            Total Count : {totalCount}
                        </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>USER ID</label>
                            <div style={inputWrapperStyle}>
                                <FaSearch style={iconStyle} />
                                <input type="text" placeholder="Search..." value={filters.searchTerm} onChange={handleSearchChange} style={{ ...modernInputStyle, border: '1px solid #cbd5e1' }} />
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>AGE</label>
                            <div style={inputWrapperStyle}>
                                <FaUser style={iconStyle} />
                                <Form.Select value={filters.ageRange} onChange={(e) => handleFilterChange('ageRange', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    <option>1-18</option><option>19-25</option><option>26-35</option><option>36-45</option><option>46-60</option><option>60+</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>GENDER</label>
                            <div style={inputWrapperStyle}>
                                <FaVenusMars style={iconStyle} />
                                <Form.Select value={filters.gender} onChange={(e) => handleFilterChange('gender', e.target.value)} style={modernInputStyle}>
                                    <option>All</option><option>Male</option><option>Female</option><option>Others</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>DEVICE</label>
                            <div style={inputWrapperStyle}>
                                <FaMobileAlt style={iconStyle} />
                                <Form.Select value={filters.deviceType} onChange={(e) => handleFilterChange('deviceType', e.target.value)} style={modernInputStyle}>
                                    <option>All</option><option>Android</option><option>iOS</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>PROFESSION</label>
                            <div style={inputWrapperStyle}>
                                <FaBriefcase style={iconStyle} />
                                <Form.Select value={filters.profession} onChange={(e) => handleFilterChange('profession', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    {reportData.allProfessions.map((p, i) => <option key={i} value={p}>{p}</option>)}
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>MOOD</label>
                            <div style={inputWrapperStyle}>
                                <MdOutlineMood style={iconStyle} />
                                <Form.Select value={filters.mood} onChange={(e) => handleFilterChange('mood', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    {Object.keys(moodEmojis).map(m => <option key={m} value={m}>{m}</option>)}
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>ENERGY</label>
                            <div style={inputWrapperStyle}>
                                <MdOutlineBolt style={iconStyle} />
                                <Form.Select value={filters.energy} onChange={(e) => handleFilterChange('energy', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    {Object.values(energyMap).map(v => <option key={v} value={v}>{v}</option>)}
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>STRESS</label>
                            <div style={inputWrapperStyle}>
                                <MdOutlineMonitorHeart style={iconStyle} />
                                <Form.Select value={filters.stress} onChange={(e) => handleFilterChange('stress', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    {Object.values(stressMap).map(v => <option key={v} value={v}>{v}</option>)}
                                </Form.Select>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Distribution Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                {/* Overall Mood Distribution */}
                <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', padding: '24px' }}>
                    <h6 style={{ fontSize: '15px', fontWeight: 800, color: '#1e293b', marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '4px solid #4f46e5', paddingLeft: '12px' }}>Overall Mood Distribution</h6>
                    <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                            {reportData.moodPieData.length === 0 ? (
                                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="#94a3b8" fontWeight="700" fontSize="16">No Data</text>
                            ) : (
                                <Pie
                                    data={reportData.moodPieData}
                                    cx="50%" cy="50%" outerRadius={100} dataKey="value" labelLine={false} label={renderCustomizedLabel}
                                >
                                    {reportData.moodPieData.map((entry, i) => (
                                        <Cell key={`cell-${i}`} fill={moodColors[entry.name] || `hsl(${(i * 360) / 12}, 75%, 60%)`} stroke="none" />
                                    ))}
                                </Pie>
                            )}
                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }} />
                            <Legend verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                        </PieChart>
                    </ResponsiveContainer>
                </Card>

                {/* Energy Level Distribution */}
                <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', padding: '24px' }}>
                    <h6 style={{ fontSize: '15px', fontWeight: 800, color: '#1e293b', marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '4px solid #f59e0b', paddingLeft: '12px' }}>Energy Level Distribution</h6>
                    <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                            {reportData.energyPieData.length === 0 ? (
                                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="#94a3b8" fontWeight="700" fontSize="16">No Data</text>
                            ) : (
                                <Pie data={reportData.energyPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" labelLine={false} label={renderCustomizedLabel}>
                                    {reportData.energyPieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={energyColors[entry.name] || '#94a3b8'} stroke="none" />
                                    ))}
                                </Pie>
                            )}
                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }} />
                            <Legend verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                        </PieChart>
                    </ResponsiveContainer>
                </Card>

                {/* Stress Level Distribution */}
                <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', padding: '24px' }}>
                    <h6 style={{ fontSize: '15px', fontWeight: 800, color: '#1e293b', marginBottom: '24px', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '4px solid #ef4444', paddingLeft: '12px' }}>Stress Level Distribution</h6>
                    <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                            {reportData.stressPieData.length === 0 ? (
                                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="#94a3b8" fontWeight="700" fontSize="16">No Data</text>
                            ) : (
                                <Pie data={reportData.stressPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" labelLine={false} label={renderCustomizedLabel}>
                                    {reportData.stressPieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={stressColors[entry.name] || '#94a3b8'} stroke="none" />
                                    ))}
                                </Pie>
                            )}
                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }} />
                            <Legend verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                        </PieChart>
                    </ResponsiveContainer>
                </Card>
            </div>

            {/* Table: All Mood Logs */}
            <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
                    <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1a1a1a' }}>All Mood Logs</h5>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <Form.Select
                            value={itemsPerPage}
                            onChange={(e) => handleItemsPerPageChange(e.target.value)}
                            style={{ width: '130px', height: '38px', fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        >
                            <option value={10}>10 records</option>
                            <option value={15}>15 records</option>
                            <option value={20}>20 records</option>
                            <option value={25}>25 records</option>
                            <option value={30}>30 records</option>
                            <option value={50}>50 records</option>
                            <option value={100}>100 records</option>
                        </Form.Select>

                        {/* <button 
                            onClick={() => setShowExportModal(true)} 
                            style={{ padding: '0 20px', height: '38px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}
                        >
                            <FaDownload /> EXPORT
                        </button> */}
                    </div>
                </div>
                <div className="scroll-container">
                    <Table hover className="sticky-table" style={{ margin: 0, tableLayout: 'fixed', minWidth: '1100px' }}>
                        <thead>
                            <tr>
                                <th className="sticky-header-th" style={{ width: '5%' }}>#</th>
                                {renderSortHeader('User ID', 'userId', '13%')}
                                {renderSortHeader('Gender', 'userGender', '10%')}
                                {renderSortHeader('Age', 'userAge', '8%')}
                                {renderSortHeader('Profession', 'userProfession', '15%')}
                                {renderSortHeader('Mood', 'mood', '15%')}
                                {renderSortHeader('Energy', 'energy', '12%')}
                                {renderSortHeader('Stress', 'stress', '12%')}
                                {renderSortHeader('Date & Time', 'createdDate', '15%')}
                            </tr>
                        </thead>
                        <tbody>
                             {loading ? (
                                <tr>
                                    <td colSpan="9" style={{ padding: '80px 0' }}>
                                        <ComponentLoader color="#4f46e5" size="40px" />
                                    </td>
                                </tr>
                            ) : (
                                reportData.tableData.map((item, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle', color: '#64748b' }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td style={{ padding: '20px 12px', fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>{item.userId}</td>
                                        <td style={{ padding: '20px 12px', fontSize: '14px', color: '#475569' }}>{(item.userGender || 'N/A').charAt(0).toUpperCase() + (item.userGender || 'N/A').slice(1)}</td>
                                        <td style={{ padding: '20px 12px', fontSize: '14px', color: '#475569' }}>{item.userAge || 'N/A'}</td>
                                        <td style={{ padding: '20px 12px', fontSize: '14px', color: '#475569' }}>{(item.userProfession || 'N/A').charAt(0).toUpperCase() + (item.userProfession || 'N/A').slice(1)}</td>
                                        <td style={{ padding: '20px 12px', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>{getMoodEmoji(item.mood)} {item.mood || 'N/A'}</td>
                                        <td style={{ padding: '20px 12px' }}>
                                            <span style={{ padding: '6px 12px', borderRadius: '8px', background: energyColors[item.energy] || '#fcd34d', color: '#fff', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>
                                                {item.energy || 'N/A'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '20px 12px' }}>
                                            <span style={{ padding: '6px 12px', borderRadius: '8px', background: stressColors[item.stress] || '#10b981', color: '#fff', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>
                                                {item.stress || 'N/A'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '20px 24px', fontSize: '14px', color: '#64748b', fontWeight: 500 }}>{formatDate(item.createdDate) || 'N/A'}</td>
                                    </tr>
                                )))}
                        </tbody>
                    </Table>
                </div>

                <div className="modern-pagination-wrapper">
                    <div className="pagination-left">
                        <div className="d-flex align-items-center gap-2">
                            <span className="pagination-label">Go to:</span>
                            <input type="number" className="pagination-input-box" value={goToPage} onChange={(e) => setGoToPage(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleGoToPage()} />
                            <button className="pagination-go-button" onClick={handleGoToPage}>Go</button>
                        </div>
                        <span className="pagination-label">Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries</span>
                    </div>
                    <div className="pagination-right">
                        <div className="ms-3 d-flex gap-2">
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(1)} 
                                disabled={currentPage === 1}
                            >
                                First
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                                disabled={currentPage === 1}
                            >
                                Prev
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(prev => prev + 1)} 
                                disabled={currentPage * itemsPerPage >= totalCount}
                            >
                                Next
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(Math.ceil(totalCount / itemsPerPage))} 
                                disabled={currentPage * itemsPerPage >= totalCount}
                            >
                                Last
                            </button>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Export Modal */}
            <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
                <Modal.Header closeButton style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Modal.Title style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                        Export Mood Analytics Report
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '10px' }}>
                            SELECT DATA RANGE
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <Form.Check
                                type="radio"
                                label={`Current Page(${reportData.tableData.length} records)`}
                                name="exportOption"
                                checked={exportOption === "currentPage"}
                                onChange={() => setExportOption("currentPage")}
                                style={{ fontSize: '14px', color: '#1e293b' }}
                            />
                            <Form.Check
                                type="radio"
                                label={`All Pages(${totalCount} records)`}
                                name="exportOption"
                                checked={exportOption === "allPages"}
                                onChange={() => setExportOption("allPages")}
                                style={{ fontSize: '14px', color: '#1e293b' }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
                        <Button
                            variant="primary"
                            disabled={isExporting}
                            onClick={() => performExport("csv")}
                            style={{ flex: 1, background: '#4f46e5', border: 'none', fontWeight: 600, height: '40px' }}
                        >
                            {isExporting ? "Processing..." : "Export as CSV"}
                        </Button>
                        <Button
                            variant="danger"
                            disabled={isExporting}
                            onClick={() => performExport("pdf")}
                            style={{ flex: 1, background: '#ef4444', border: 'none', fontWeight: 600, height: '40px' }}
                        >
                            {isExporting ? "Processing..." : "Export as PDF"}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

        </div>
    );
};

export default MoodAnalytics;
